import Helper from '@ember/component/helper';
import { type Registry as Services, service } from '@ember/service';
import type { GeneralPropertyFeature } from 'lumen/utils/constants/unified-settings-feature-constants';

interface HasUnifiedSettingsActiveFeatureSignature {
  Args: {
    Positional: [GeneralPropertyFeature];
  };
  Return: boolean;
}

/**
 * Checks whether a Unified Settings feature is active for the current
 * subscription. Used in templates to hide fields that are not yet "unified"
 * for a subscription and must still be managed in legacy Channel Manager.
 *
 * Unified Settings uses its own flag system (separate from the in-app one) so
 * rollout stays in sync with Stardekk and can be activated per-subscription.
 * Flags are loaded by `subscription-active-features` from
 * `GET /v3/channel-management/unified-settings/active-features`.
 *
 * ```hbs
 * {{#if (hasUnifiedSettingsActiveFeature "general-property-hotel-name")}} ... {{/if}}
 * ```
 *
 * See INDU-1353 and Confluence "1) General Property configuration"(https://lighthouseintelligence.atlassian.net/wiki/spaces/IP/pages/6267273267/1+General+Property+configuration#Feature-set-%26-feature-names)
 * for the feature-name catalogue.
 */
export default class HasUnifiedSettingsActiveFeatureHelper extends Helper<HasUnifiedSettingsActiveFeatureSignature> {
  @service('subscription-active-features')
  declare activeFeatures: Services['subscription-active-features'];

  compute([
    featureName,
  ]: HasUnifiedSettingsActiveFeatureSignature['Args']['Positional']): HasUnifiedSettingsActiveFeatureSignature['Return'] {
    return this.activeFeatures.hasFeature(featureName);
  }
}
